import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Lock, ArrowRight, LifeBuoy, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { billingMessage } from "@/components/BillingStatusCard";

const OPEN_WHEN_LOCKED = ["/billing", "/support"];

function BillingBanner({ st }) {
  const navigate = useNavigate();
  const final = st.state === "final_warning";
  return (
    <div data-testid="billing-banner" className={"flex items-center gap-3 px-6 py-2.5 text-sm border-b " + (final ? "bg-red-500/10 border-red-500/30 text-red-100" : "bg-amber-500/10 border-amber-500/25 text-amber-100")}>
      <AlertTriangle className={"h-4 w-4 shrink-0 " + (final ? "text-red-400" : "text-amber-400")} />
      <p data-testid="billing-banner-text" className="min-w-0">{billingMessage(st)}</p>
      <button data-testid="billing-banner-action" onClick={() => navigate("/billing")} className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border border-current/40 hover:bg-white/10 transition-colors">
        {st.reason === "payment_failed" ? "Update payment" : "Subscribe now"} <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function LockScreen({ st }) {
  const navigate = useNavigate();
  return (
    <div data-testid="billing-lock-screen" className="flex-1 flex items-center justify-center p-6">
      <div className="max-w-lg text-center">
        <div className="h-16 w-16 mx-auto rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center"><Lock className="h-8 w-8 text-red-400" /></div>
        <h1 className="mt-6 font-display text-2xl font-bold">Your account is locked</h1>
        <p data-testid="billing-lock-text" className="mt-3 text-gray-400 leading-relaxed">{billingMessage(st)}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button data-testid="lock-go-billing" onClick={() => navigate("/billing")} className="gold-gradient text-black font-semibold px-5 py-2.5 rounded-xl inline-flex items-center gap-2">
            {st.reason === "payment_failed" ? "Update payment" : "Choose a plan"} <ArrowRight className="h-4 w-4" />
          </button>
          <button data-testid="lock-go-support" onClick={() => navigate("/support")} className="px-5 py-2.5 rounded-xl border border-gray-700 text-gray-300 hover:text-white inline-flex items-center gap-2">
            <LifeBuoy className="h-4 w-4" /> Contact support
          </button>
        </div>
      </div>
    </div>
  );
}

// Shows payment warnings and blocks the portal (except Billing/Support) when locked.
export function BillingGate({ user, children }) {
  const { pathname } = useLocation();
  const [st, setSt] = useState(null);
  const skip = user.role === "admin" || user.impersonating;

  useEffect(() => {
    if (skip) return;
    api.get("/billing/state").then((r) => setSt(r.data)).catch(() => {});
  }, [pathname, skip]);

  if (skip || !st) return children;
  if (st.state === "locked" && !OPEN_WHEN_LOCKED.some((p) => pathname.startsWith(p))) return <LockScreen st={st} />;
  return (
    <>
      {(st.state === "warning" || st.state === "final_warning") && <BillingBanner st={st} />}
      {st.state === "active" && st.reason === "trial" && <TrialNotice days={st.days_left} />}
      {children}
    </>
  );
}

function TrialNotice({ days }) {
  const navigate = useNavigate();
  return (
    <div data-testid="trial-banner" className="flex items-center gap-3 px-6 py-2.5 text-sm border-b bg-amber-500/10 border-amber-500/25 text-amber-100">
      <Sparkles className="h-4 w-4 shrink-0 text-amber-400" />
      <p data-testid="trial-banner-text"><span className="font-semibold">{days} day{days === 1 ? "" : "s"} left</span> in your free trial. Subscribe anytime — you won't be charged until the trial ends.</p>
      <button data-testid="trial-banner-upgrade" onClick={() => navigate("/billing")} className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border border-amber-500/40 text-amber-200 hover:bg-amber-500/15">
        Choose a plan <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
